import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import ReferenceComponent from '../components/ReferenceComponent';

export interface ReferenceOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    reference: {
      /**
       * Set a reference
       */
      setReference: (id: string) => ReturnType;
    };
  }
}

export const ReferencePluginKey = new PluginKey('referencePlugin');

export const Reference = Node.create<ReferenceOptions>({
  name: 'reference',

  group: 'inline',

  inline: true,

  selectable: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          // Try getting the data-id attribute first
          const dataId = element.getAttribute('data-id');
          if (dataId) return dataId;

          // If no data-id, check if the content is a template reference
          const content = element.textContent;
          if (content) {
            // Handle any content inside {{ }}
            const match = content.match(/^\{\{(.+?)\}\}$/);
            if (match) {
              return match[1];
            }
          }

          return null;
        },
        renderHTML: (attributes: { id: string }) => {
          return {
            'data-id': attributes.id,
          };
        },
        toJSON: (attributes: { id: string }) => attributes.id,
        parseJSON: (value: string) => value,
      },
    };
  },

  parseHTML() {
    return [
      // Standard data-reference elements
      {
        tag: 'span[data-reference]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          const element = node as HTMLElement;
          return { id: element.getAttribute('data-id') };
        },
      },
      // Text that directly contains a reference in template format
      {
        tag: 'span',
        getAttrs: (node) => {
          if (typeof node === 'string') return false;
          const element = node as HTMLElement;
          const text = element.textContent || '';

          // Match any content between {{ and }}
          const match = text.match(/^\{\{(.+?)\}\}$/);
          if (match) {
            return { id: match[1] };
          }

          return false;
        },
      },
      // Just to be extra safe, also check for paragraphs or divs that contain only a reference
      {
        tag: 'p, div',
        getAttrs: (node) => {
          if (typeof node === 'string') return false;
          const element = node as HTMLElement;

          // Only match if this element contains nothing but a reference
          if (element.childNodes.length !== 1) return false;

          const text = element.textContent?.trim() || '';
          const match = text.match(/^\{\{(.+?)\}\}$/);
          if (match) {
            return { id: match[1] };
          }

          return false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-reference': '' }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ReferenceComponent);
  },

  toJSON(node: ProseMirrorNode) {
    return {
      type: this.name,
      attrs: {
        id: node.attrs.id,
      },
    };
  },

  parseJSON(json: { attrs: { id: string } }) {
    return {
      type: this.name,
      attrs: {
        id: json.attrs.id,
      },
    };
  },

  addCommands() {
    return {
      setReference:
        (id) =>
        ({ chain, editor, dispatch }) => {
          const result = chain()
            .insertContent([
              {
                type: this.name,
                attrs: {
                  id,
                },
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();

          if (result && editor && dispatch) {
            const { state } = editor;
            const { selection } = state;

            try {
              const tr = state.tr.setSelection(
                TextSelection.create(state.doc, selection.anchor)
              );

              dispatch(tr);
              editor.view.focus();

              setTimeout(() => {
                const domSelection = window.getSelection();
                if (
                  domSelection &&
                  editor.view.dom.contains(
                    editor.view.dom.ownerDocument.activeElement
                  )
                ) {
                  const endPos = editor.state.selection.anchor;

                  dispatch(
                    editor.state.tr.setSelection(
                      TextSelection.create(state.doc, endPos - 1)
                    )
                  );

                  setTimeout(() => {
                    dispatch(
                      editor.state.tr.setSelection(
                        TextSelection.create(editor.state.doc, endPos)
                      )
                    );
                    editor.view.focus();
                  }, 10);
                }
              }, 20);
            } catch (e) {
              // Silent error handling
            }
          }

          return result;
        },
    };
  },

  addProseMirrorPlugins() {
    const referencePluginKey = ReferencePluginKey;

    return [
      new Plugin({
        key: referencePluginKey,
        props: {
          handleDOMEvents: {
            click: (view, event) => {
              const target = event.target as HTMLElement;
              const reference = target.closest('[data-reference]');

              if (reference) {
                const id = reference.getAttribute('data-id');
                if (id) {
                  return true;
                }
              }

              return false;
            },
          },
        },
        appendTransaction(transactions, oldState, newState) {
          return null;
        },
      }),
    ];
  },
});
